/**
 * Fase 4 - Relatórios e Gestão para Empregadores
 * 
 * Dashboard com:
 * - Resumo agregado da equipe
 * - Comparação anônima de trabalhadores
 * - Histórico de produção
 * 
 * Baseado no Tripé:
 * - ⚖️ Justiça: Dados objetivos
 * - 🔒 Segurança: Comparações anônimas
 * - ✨ Simplicidade: Visualização clara
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { useTranslation } from '../translations';

const API = process.env.REACT_APP_BACKEND_URL;

const ReportsDashboard = ({ user }) => {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [teamSummary, setTeamSummary] = useState(null);
  const [teamComparison, setTeamComparison] = useState(null);
  const [productionHistory, setProductionHistory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('summary');
  const [showTip, setShowTip] = useState(true);
  const { t } = useTranslation();
  
  // Filtros de período
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const loadProperties = async () => {
    try {
      const res = await axios.get(`${API}/api/reports/my-properties`);
      setProperties(res.data.properties || []);
      if (res.data.properties?.length > 0) {
        setSelectedProperty(res.data.properties[0].id);
      }
    } catch (error) {
      console.error('Erro ao carregar propriedades:', error);
    }
  };

  const loadReportData = async () => {
    if (!selectedProperty) return;
    
    setLoading(true);
    try {
      const [summaryRes, comparisonRes, historyRes] = await Promise.all([
        axios.get(`${API}/api/reports/team-summary/${selectedProperty}?month=${selectedMonth}&year=${selectedYear}`),
        axios.get(`${API}/api/reports/team-comparison/${selectedProperty}?month=${selectedMonth}&year=${selectedYear}`),
        axios.get(`${API}/api/reports/production-history/${selectedProperty}?months=6`)
      ]);
      
      setTeamSummary(summaryRes.data);
      setTeamComparison(comparisonRes.data);
      setProductionHistory(historyRes.data);
    } catch (error) {
      console.error('Erro ao carregar relatórios:', error);
    }
    setLoading(false);
  };

  // Carregar propriedades do empregador
  useEffect(() => {
    loadProperties();
  }, []);

  // Carregar dados quando propriedade é selecionada
  useEffect(() => {
    if (selectedProperty) {
      loadReportData();
    }
  }, [selectedProperty, selectedMonth, selectedYear]);

  const months = [
    { value: 1, label: t('january') },
    { value: 2, label: t('february') },
    { value: 3, label: t('march') },
    { value: 4, label: t('april') },
    { value: 5, label: t('may') },
    { value: 6, label: t('june') },
    { value: 7, label: t('july') },
    { value: 8, label: t('august') },
    { value: 9, label: t('september') },
    { value: 10, label: t('october') },
    { value: 11, label: t('november') },
    { value: 12, label: t('december') }
  ];

  return (
    <div className="space-y-6">
      {/* Tooltip Como Usar */}
      {showTip && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 relative">
          <button 
            onClick={() => setShowTip(false)}
            className="absolute top-2 right-2 text-indigo-400 hover:text-indigo-600"
          >
            ✕
          </button>
          <div className="flex items-start gap-2 pr-6">
            <span className="text-lg">📈</span>
            <div>
              <h4 className="font-semibold text-indigo-800">{t('howToUse')}</h4>
              <p className="text-sm text-indigo-700 mt-1">💡 {t('reportsViewStats')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl shadow-lg p-6 text-white">
        <h2 className="text-2xl font-bold mb-2">📊 {t('reportsAndManagement')}</h2>
        <p className="opacity-90">{t('trackTeamPerformance')}</p>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4 items-center">
            {/* Seletor de Propriedade */}
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">{t('property')}</label>
              <select
                value={selectedProperty || ''}
                onChange={(e) => setSelectedProperty(e.target.value)}
                className="border rounded-lg px-3 py-2 min-w-[200px]"
              >
                {properties.map(prop => (
                  <option key={prop.id} value={prop.id}>{prop.name}</option>
                ))}
              </select>
            </div>

            {/* Seletor de Mês */}
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">{t('month')}</label>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="border rounded-lg px-3 py-2"
              >
                {months.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            {/* Seletor de Ano */}
            <div>
              <label className="text-sm font-medium text-gray-600 block mb-1">{t('year')}</label>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="border rounded-lg px-3 py-2"
              >
                {[2024, 2025, 2026].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>

            <Button 
              onClick={loadReportData} 
              className="mt-5"
              disabled={loading}
            >
              {loading ? t('loading') : `🔄 ${t('update')}`}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="border-b">
        <div className="flex space-x-4">
          <button
            onClick={() => setActiveTab('summary')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'summary' 
                ? 'border-indigo-500 text-indigo-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📈 {t('teamSummary')}
          </button>
          <button
            onClick={() => setActiveTab('comparison')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'comparison' 
                ? 'border-indigo-500 text-indigo-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            🏆 {t('anonymousRanking')}
          </button>
          <button
            onClick={() => setActiveTab('history')}
            className={`px-4 py-2 font-medium border-b-2 transition-colors ${
              activeTab === 'history' 
                ? 'border-indigo-500 text-indigo-600' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            📅 {t('history')}
          </button>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        </div>
      )}

      {/* Tab: Resumo da Equipe */}
      {activeTab === 'summary' && !loading && teamSummary && (
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-indigo-600">
                  {teamSummary.summary?.total_workers || 0}
                </div>
                <div className="text-sm text-gray-600 mt-1">👷 {t('workers')}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-emerald-600">
                  {teamSummary.summary?.total_production_kg?.toFixed(1) || 0} kg
                </div>
                <div className="text-sm text-gray-600 mt-1">🌿 {t('totalProduction')}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-amber-600">
                  {teamSummary.summary?.workers_with_seal || 0}
                </div>
                <div className="text-sm text-gray-600 mt-1">🏅 {t('withSeal')}</div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="pt-6">
              <div className="text-center">
                <div className="text-4xl font-bold text-purple-600">
                  {teamSummary.summary?.average_score?.toFixed(1) || 0}
                </div>
                <div className="text-sm text-gray-600 mt-1">📊 {t('averageScore')}</div>
              </div>
            </CardContent>
          </Card>

          {/* Card de Detalhes */}
          <Card className="md:col-span-2 lg:col-span-4">
            <CardHeader>
              <CardTitle>{t('periodDetails')}</CardTitle>
              <CardDescription>
                {months.find(m => m.value === teamSummary.period?.month)?.label} de {teamSummary.period?.year}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="p-4 bg-green-50 rounded-lg">
                  <div className="text-sm text-gray-600">Dias de Sangria</div>
                  <div className="text-2xl font-bold text-green-700">{teamSummary.summary?.sangria_days || 0}</div>
                </div>
                <div className="p-4 bg-blue-50 rounded-lg">
                  <div className="text-sm text-gray-600">Dias de Reposição</div>
                  <div className="text-2xl font-bold text-blue-700">{teamSummary.summary?.reposicao_days || 0}</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600">{t('dailyAverage')}</div>
                  <div className="text-2xl font-bold">{teamSummary.summary?.average_daily_production?.toFixed(2) || 0} kg</div>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg">
                  <div className="text-sm text-gray-600">{t('certified')}</div>
                  <div className="text-2xl font-bold">{teamSummary.summary?.workers_certified || 0}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab: Ranking Anônimo */}
      {activeTab === 'comparison' && !loading && teamComparison && (
        <Card>
          <CardHeader>
            <CardTitle>🏆 {t('productionRanking')}</CardTitle>
            <CardDescription>
              {t('anonymousComparisonProtected')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {teamComparison.comparisons?.length > 0 ? (
              <div className="space-y-3">
                {teamComparison.comparisons.map((worker, idx) => (
                  <div 
                    key={idx}
                    className={`flex items-center justify-between p-4 rounded-lg border ${
                      idx === 0 ? 'bg-yellow-50 border-yellow-200' :
                      idx === 1 ? 'bg-gray-50 border-gray-200' :
                      idx === 2 ? 'bg-orange-50 border-orange-200' :
                      'bg-white border-gray-100'
                    }`}
                  >
                    <div className="flex items-center space-x-4">
                      <span className="text-2xl font-bold text-gray-400">
                        #{worker.rank}
                      </span>
                      <div>
                        <div className="font-semibold flex items-center space-x-2">
                          <span>{worker.worker_label}</span>
                          {worker.seal_emoji && (
                            <span title={`${t('seal')} ${worker.seal}`}>{worker.seal_emoji}</span>
                          )}
                        </div>
                        <div className="text-sm text-gray-600">
                          {worker.work_days} dias de sangria
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-emerald-600">
                        {worker.total_production_kg?.toFixed(1)} kg
                      </div>
                      <div className="text-sm text-gray-500">
                        {t('average')}: {worker.average_daily_kg?.toFixed(2)} kg/{t('days').toLowerCase()}
                      </div>
                      {worker.technical_score && (
                        <Badge className="mt-1 bg-indigo-100 text-indigo-800">
                          Score: {worker.technical_score}
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <span className="text-4xl mb-4 block">📋</span>
                <p>{t('noProductionDataPeriod')}</p>
                <p className="text-sm mt-2">{t('dataAppearsWhenWorkersRegister')}</p>
              </div>
            )}
            
            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-700">
              💡 <strong>{t('privacyNotice')}</strong> {t('realNamesProtected')}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tab: Histórico */}
      {activeTab === 'history' && !loading && productionHistory && (
        <Card>
          <CardHeader>
            <CardTitle>📅 {t('productionHistory')}</CardTitle>
            <CardDescription>
              {t('lastMonthsAnalyzed').replace('{months}', productionHistory.months_analyzed)}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {productionHistory.history?.length > 0 ? (
              <div className="space-y-4">
                {/* Gráfico simples de barras */}
                <div className="flex items-end space-x-2 h-48 border-b border-l p-4">
                  {productionHistory.history.map((month, idx) => {
                    const maxProduction = Math.max(...productionHistory.history.map(m => m.total_production_kg || 1));
                    const heightPercent = ((month.total_production_kg || 0) / maxProduction) * 100;
                    
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-indigo-500 rounded-t transition-all duration-500"
                          style={{ height: `${Math.max(heightPercent, 5)}%` }}
                          title={`${month.total_production_kg?.toFixed(1)} kg`}
                        ></div>
                        <div className="text-xs mt-2 text-gray-600">{month.month_name}</div>
                      </div>
                    );
                  })}
                </div>

                {/* Tabela detalhada */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">{t('period')}</th>
                        <th className="text-right py-2">{t('production')}</th>
                        <th className="text-right py-2">{t('days')}</th>
                        <th className="text-right py-2">{t('workers')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {productionHistory.history.map((month, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="py-2">{month.month_name}/{month.year}</td>
                          <td className="text-right py-2 font-medium">
                            {month.total_production_kg?.toFixed(1) || 0} kg
                          </td>
                          <td className="text-right py-2">{month.work_days}</td>
                          <td className="text-right py-2">{month.workers_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <span className="text-4xl mb-4 block">📊</span>
                <p>{t('noHistoryAvailable')}</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Mensagem quando não há propriedades */}
      {properties.length === 0 && !loading && (
        <Card>
          <CardContent className="py-8 text-center">
            <span className="text-4xl mb-4 block">🏠</span>
            <p className="text-gray-600">{t('noPropertiesYet')}</p>
            <p className="text-sm text-gray-500 mt-2">
              {t('registerPropertyToSeeReports')}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ReportsDashboard;
